// Footer: Professional footer for all pages
import React, { useContext, useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography, Link, Grid, Stack, Avatar, IconButton, useTheme, Divider } from '@mui/material';
import { SettingsContext } from '../context/SettingsContext';
import api from '../services/api';

function Footer() {
  const settings = useContext(SettingsContext);
  const theme = useTheme();
  const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const resolveAsset = (url, fallback) => {
    if (!url) return fallback;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('/')) return `${API_BASE}${url}`;
    return url;
  };
  const logoSrc = resolveAsset(settings?.logoUrl, `${API_BASE}/api/public/logo`);
  const platformName = settings?.platformName || 'Timte 2026';
  const footerText = settings?.footerText || `© ${new Date().getFullYear()} ${platformName}. All rights reserved.`;
  const companyCredit = 'Tanzania International Medical Tourism Expo';
  const [sponsors, setSponsors] = useState([]);
  const [loadingSponsors, setLoadingSponsors] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoadingSponsors(true);
    api.get('/api/sponsors').then(res => {
      if (!mounted) return;
      setSponsors(Array.isArray(res.data) ? res.data.slice(0, 8) : []);
    }).catch(() => {
      if (!mounted) return;
      setSponsors([]);
    }).finally(() => { if (mounted) setLoadingSponsors(false); });
    return () => { mounted = false; };
  }, []);

  const quickLinksLeft = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Packages', path: '/packages' },
    { name: 'Accommodation', path: '/accommodation' },
  ];
  const quickLinksRight = [
    { name: 'Schedule', path: '/schedule' },
    { name: 'Highlights', path: '/highlights' },
    { name: 'VIPs', path: '/vips' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? '#0f1220' : 'linear-gradient(90deg, #00a679 0%, #00c896 60%, #ec2127 100%)',
        color: theme.palette.mode === 'dark' ? undefined : '#ffffff',
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4 },
        mt: 6,
        borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'}`,
        position: 'relative',
        overflow: 'hidden',
        // keyframes for subtle floating animation
        '@keyframes floatA': {
          '0%': { transform: 'translateY(0) rotate(-2deg)' },
          '50%': { transform: 'translateY(-12px) rotate(2deg)' },
          '100%': { transform: 'translateY(0) rotate(-2deg)' }
        },
        '@keyframes floatB': {
          '0%': { transform: 'translateY(0) rotate(1deg)' },
          '50%': { transform: 'translateY(-8px) rotate(-1deg)' },
          '100%': { transform: 'translateY(0) rotate(1deg)' }
        },
        '@keyframes floatC': {
          '0%': { transform: 'translateY(0) rotate(0deg)' },
          '50%': { transform: 'translateY(-10px) rotate(3deg)' },
          '100%': { transform: 'translateY(0) rotate(0deg)' }
        },
        // respect reduced motion
        '@media (prefers-reduced-motion: reduce)': {
          '& .anim-footer': { animation: 'none !important' }
        }
      }}
      component="footer"
    >
      {/* Decorative floating animals in the footer background (subtle, non-interactive) */}
      <Box className="anim-footer" sx={{
        position: 'absolute',
        left: { xs: -8, md: -28 },
        bottom: { xs: -6, md: -14 },
        width: { xs: 140, md: 260 },
        height: { xs: 96, md: 170 },
        backgroundImage: 'url(https://i.postimg.cc/2VK8k9rZ/girafe.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom',
        backgroundSize: { xs: '120px', md: '260px' },
        opacity: 0.16,
        pointerEvents: 'none',
        zIndex: 0,
        filter: 'saturate(1.12) contrast(1.08) drop-shadow(0 8px 22px rgba(0,0,0,0.28))',
        mixBlendMode: theme.palette.mode === 'dark' ? 'screen' : 'multiply',
        animation: 'floatA 12s ease-in-out infinite'
      }} />

      <Box className="anim-footer" sx={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: { xs: -12, md: -26 },
        width: { xs: 200, md: 360 },
        height: { xs: 120, md: 210 },
        backgroundImage: 'url(https://i.postimg.cc/mt52LpBS/lion.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom',
        backgroundSize: { xs: '180px', md: '360px' },
        opacity: theme.palette.mode === 'dark' ? 0.14 : 0.18,
        pointerEvents: 'none',
        zIndex: 0,
        filter: 'saturate(1.14) contrast(1.12) drop-shadow(0 12px 30px rgba(0,0,0,0.32)) blur(0.2px)',
        mixBlendMode: theme.palette.mode === 'dark' ? 'screen' : 'multiply',
        animation: 'floatB 14s ease-in-out infinite'
      }} />

      <Box className="anim-footer" sx={{
        position: 'absolute',
        right: { xs: -8, md: -28 },
        bottom: { xs: -6, md: -14 },
        width: { xs: 140, md: 260 },
        height: { xs: 96, md: 170 },
        backgroundImage: 'url(https://i.postimg.cc/3dcJ8fY0/zebra.png)',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center bottom',
        backgroundSize: { xs: '120px', md: '260px' },
        opacity: 0.16,
        pointerEvents: 'none',
        zIndex: 0,
        filter: 'saturate(1.12) contrast(1.08) drop-shadow(0 8px 22px rgba(0,0,0,0.28))',
        mixBlendMode: theme.palette.mode === 'dark' ? 'screen' : 'multiply',
        animation: 'floatC 13s ease-in-out infinite'
      }} />
      <Grid container spacing={3} alignItems="flex-start" sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            {settings ? (
              <img src={logoSrc} alt={platformName} style={{ height: 48, objectFit: 'contain' }} />
            ) : (
              <CircularProgress size={40} sx={{ mr: 2 }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.mode === 'dark' ? '#fff' : 'text.primary' }}>{platformName}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{companyCredit}</Typography>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.7, fontWeight: 500 }}>
            {settings?.shortDescription || 'Welcome to the TIMTE Expo, a major healthcare event in Tanzania showcasing advanced local medical services. It brings together leading hospitals, sponsors, exhibitors, and innovators to promote medical excellence, partnerships, and reduce the need for treatment abroad.'}
          </Typography>
          <Stack spacing={0.5}>
            {settings?.address && <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>{settings.address}</Typography>}
            {settings?.phone && <Link href={`tel:${settings.phone}`} sx={{ color: '#00a679', fontWeight: 600, transition: 'all 250ms ease', '&:hover': { color: '#ec2127', textShadow: '0 0 8px rgba(236,33,39,0.3)' } }}>{settings.phone}</Link>}
            {settings?.email && <Link href={`mailto:${settings.email}`} sx={{ color: '#00a679', fontWeight: 600, transition: 'all 250ms ease', '&:hover': { color: '#ec2127', textShadow: '0 0 8px rgba(236,33,39,0.3)' } }}>{settings.email}</Link>}
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#00a679', fontSize: '1.05rem', borderLeft: '3px solid #00a679', pl: 1 }}>Quick Links</Typography>
              <Stack spacing={0.8}>
                {quickLinksLeft.map(l => (
                  <Link key={l.name} href={l.path} underline="none" sx={{ color: 'text.secondary', fontWeight: 600, transition: 'all 250ms ease', '&:hover': { color: '#00a679', transform: 'translateX(4px)', textShadow: '0 0 8px rgba(0,166,121,0.3)' } }}>{l.name}</Link>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#00a679', fontSize: '1.05rem', borderLeft: '3px solid #00a679', pl: 1 }}>More</Typography>
              <Stack spacing={0.8}>
                {quickLinksRight.map(l => (
                  <Link key={l.name} href={l.path} underline="none" sx={{ color: 'text.secondary', fontWeight: 600, transition: 'all 250ms ease', '&:hover': { color: '#00a679', transform: 'translateX(4px)', textShadow: '0 0 8px rgba(0,166,121,0.3)' } }}>{l.name}</Link>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2, color: '#00a679', fontSize: '1.05rem', borderLeft: '3px solid #00a679', pl: 1 }}>Expo Sponsors</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {loadingSponsors ? (
              <CircularProgress size={24} sx={{ color: '#00a679' }} />
            ) : sponsors.length === 0 ? (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>No sponsors available</Typography>
            ) : (
              sponsors.map((s, i) => (
                <Box
                  key={i}
                  title={s.name}
                  sx={{
                    width: { xs: 32, sm: 44 },
                    height: { xs: 32, sm: 44 },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: theme.palette.mode === 'dark' ? '#0b0c10' : '#fff',
                    boxShadow: '0 2px 8px rgba(0,166,121,0.12)',
                    border: '2px solid #00a679',
                    transition: 'transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease',
                    '&:hover': { transform: 'scale(1.12)', boxShadow: '0 6px 20px rgba(0,166,121,0.3)', borderColor: '#ec2127' }
                  }}
                >
                  <img src={s.logo} alt={s.name} style={{ width: '70%', height: '70%', objectFit: 'contain' }} />
                </Box>
              ))
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 2, borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eee' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: 1400, mx: 'auto', px: { xs: 0, md: 0 } }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{footerText}</Typography>

      </Box>
    </Box>
  );
}

export default Footer;
